<template>
    <div ref="elementRef" class="pixels">
        <slot :click="handleClick"/>
    </div>
</template>

<script setup lang="ts">
import { watch, ref, PropType, onBeforeUnmount, onMounted } from 'vue';
import AdnuntiusBulkService from './adnuntiusBulk.service';
import gtmService from '@/project/tracking/gtm/GTM.service';
import { AdnuntiusTrackingData } from '@/project/tracking/adnuntius/types';

const props = defineProps({
    renderedUrl: {
        type: String,
        required: false,
        default: undefined,
    },
    visibleUrl: {
        type: String,
        required: false,
        default: undefined,
    },
    viewUrl: {
        type: String,
        required: false,
        default: undefined,
    },
    clickUrl: {
        type: String,
        required: false,
        default: undefined,
    },
    alwaysVisible: {
        type: Boolean,
        required: false,
        default: false
    },
    trackingData: {
        type: Object as PropType<AdnuntiusTrackingData | null>,
        required: false,
        default: null,
    }
});

const isSetup = ref(false);
const isVisible = ref(false);
const isMaybeViewed = ref(false);
const isClicked = ref(false);
const observer = ref<IntersectionObserver | null>(null);
const elementRef = ref<HTMLDivElement | null>(null);

function handleClick() {
    if (!isClicked.value) {
        isClicked.value = true;
        forwardContentRequests(props.clickUrl);
        trackCreativeClick();
    }
}

function forwardContentRequests(url: string | undefined) {
    if (url) { AdnuntiusBulkService.requestData(url); }
}

function trackCreativeView() {
    if (props.trackingData) {
        gtmService.trackCreativeView([{
            name: props.trackingData.lineItemId,
            id: props.trackingData.creativeId,
            creative: props.trackingData.parentBlockAlias
        }]);
    }
}

function trackCreativeClick() {
    if (props.trackingData) {
        gtmService.trackCreativeClick(props.trackingData.lineItemId, props.trackingData.creativeId, props.trackingData.parentBlockAlias);
    }
}

function setup() {
    // Setup only when has been mounted and link props are present
    if (isSetup.value || !props.renderedUrl || !elementRef.value) return;
    isSetup.value = true;

    forwardContentRequests(props.renderedUrl);

    if (props.alwaysVisible) {
        forwardContentRequests(props.viewUrl);
        forwardContentRequests(props.visibleUrl);
    } else {
        observer.value = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        if (!isVisible.value) {
                            isVisible.value = true;
                            forwardContentRequests(props.visibleUrl);
                        }
                    } else {
                        isMaybeViewed.value = false;
                    }

                    if (entry.intersectionRatio >= 0.5 && entry.isIntersecting) {
                        isMaybeViewed.value = true;
                        setTimeout(() => {
                            if (isMaybeViewed.value) {
                                forwardContentRequests(props.viewUrl);
                                trackCreativeView();
                                unObserve();
                            }
                        }, 1000);
                    }
                });
            },
            {
                threshold: [0, 0.5]
            });

        observer.value.observe(elementRef.value);
    }
}

function unObserve(): void {
    if (observer.value) {
        observer.value.disconnect();
        observer.value = null;
    }
}

onBeforeUnmount(() => {
    unObserve();
});

onMounted(() => {
    setup();
});

watch(() =>
    props.renderedUrl,
    (url:string) => {
       if (url) {
           setup();
       }
    }
);
</script>
